import React, { useState } from 'react';
import { PersonalDetails, MARITAL_STATUS_OPTIONS, COUNTRIES } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { FormSelect } from '../common/FormSelect';
import { FormDatePicker } from '../common/FormDatePicker';
import { ListInput } from '../common/ListInput';
import { PhoneInput } from '../common/PhoneInput';

interface PersonalDetailsSectionProps {
  data: PersonalDetails;
  onUpdate: (data: PersonalDetails) => void;
}

export const PersonalDetailsSection: React.FC<PersonalDetailsSectionProps> = ({
  data,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<PersonalDetails>(data);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleEdit = () => {
    setEditData(data);
    setIsEditing(true);
    setErrors({});
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
    setErrors({});
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateURL = (url: string) => {
    if (!url || url.trim() === '') return true; // Optional field
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    // Only validate email format if email is provided
    if (editData.email.trim() && !validateEmail(editData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate external profile URL if provided
    if (editData.external_profile_url && editData.external_profile_url.trim() && !validateURL(editData.external_profile_url)) {
      newErrors.external_profile_url = 'Invalid URL format. URL must start with http:// or https://';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate(editData);
    setIsEditing(false);
    setErrors({});
  };

  const handleInputChange = (field: keyof PersonalDetails) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setEditData({ ...editData, [field]: e.target.value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleListChange = (field: 'languages_known' | 'mobile_numbers') => (items: string[]) => {
    setEditData({ ...editData, [field]: items });
  };

  const formatDate = (date: string) => {
    if (!date) return 'Not specified';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <EditableSection
      title="Personal Details"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Name"
            name="name"
            value={editData.name}
            onChange={handleInputChange('name')}
            maxLength={40}
            error={errors.name}
          />
          <FormInput
            label="Email"
            name="email"
            type="email"
            value={editData.email}
            onChange={handleInputChange('email')}
            error={errors.email}
          />
          <FormInput
            label="External Profile URL"
            name="external_profile_url"
            type="url"
            value={editData.external_profile_url || ''}
            onChange={handleInputChange('external_profile_url')}
            placeholder="https://linkedin.com/in/username"
            error={errors.external_profile_url}
          />
          <FormDatePicker
            label="Date of Birth"
            name="date_of_birth"
            value={editData.date_of_birth}
            onChange={handleInputChange('date_of_birth')}
            error={errors.date_of_birth}
            max={new Date().toISOString().split('T')[0]}
          />
          <FormSelect
            label="Marital Status"
            name="marital_status"
            value={editData.marital_status}
            onChange={handleInputChange('marital_status')}
            options={[...MARITAL_STATUS_OPTIONS]}
            placeholder="Select marital status"
          />
          <FormSelect
            label="Nationality"
            name="nationality"
            value={editData.nationality}
            onChange={handleInputChange('nationality')}
            options={COUNTRIES}
            placeholder="Select nationality"
          />
          <FormInput
            label="Current Location"
            name="present_location"
            value={editData.present_location}
            onChange={handleInputChange('present_location')}
            maxLength={50}
          />
          <div className="md:col-span-2">
            <FormInput
              label="Address"
              name="address"
              value={editData.address}
              onChange={handleInputChange('address')}
              maxLength={100}
            />
          </div>
          <div className="md:col-span-2">
            <ListInput
              label="Languages Known"
              items={editData.languages_known}
              onItemsChange={handleListChange('languages_known')}
              placeholder="Add language"
            />
          </div>
          <div className="md:col-span-2">
            <PhoneInput
              label="Phone Numbers"
              phones={editData.mobile_numbers}
              onPhonesChange={handleListChange('mobile_numbers')}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Name</p>
            <p className="mt-1 text-sm text-gray-900">{data.name || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Email</p>
            <p className="mt-1 text-sm text-gray-900">{data.email || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">External Profile</p>
            {data.external_profile_url ? (
              <a
                href={data.external_profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {data.external_profile_url.includes('linkedin.com') ? 'LinkedIn Profile' :
                 data.external_profile_url.includes('github.com') ? 'GitHub Profile' :
                 'View Profile'}
              </a>
            ) : (
              <p className="mt-1 text-sm text-gray-900">Not specified</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Date of Birth</p>
            <p className="mt-1 text-sm text-gray-900">{formatDate(data.date_of_birth)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Marital Status</p>
            <p className="mt-1 text-sm text-gray-900">{data.marital_status || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Nationality</p>
            <p className="mt-1 text-sm text-gray-900">{data.nationality || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Current Location</p>
            <p className="mt-1 text-sm text-gray-900">{data.present_location || 'Not specified'}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-gray-500">Address</p>
            <p className="mt-1 text-sm text-gray-900">{data.address || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Languages Known</p>
            {data.languages_known.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-2">
                {data.languages_known.map((lang, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-100 rounded-md text-sm text-gray-700">
                    {lang}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-900">None specified</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Phone Numbers</p>
            {data.mobile_numbers.length > 0 ? (
              <div className="mt-1 space-y-1">
                {data.mobile_numbers.map((phone, idx) => (
                  <p key={idx} className="text-sm text-gray-900">{phone}</p>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-900">None specified</p>
            )}
          </div>
        </div>
      )}
    </EditableSection>
  );
};